import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Quests.cold.css';
import { flyRewardsToHeader } from '../utils/flyToHeader';
import { useSound } from '../sfx/SoundProvider';

// === API base ===
const API_ORIGIN = process.env.REACT_APP_API_URL;
const API_ROOT   = `${API_ORIGIN}/api`;
const API_HOST   = API_ORIGIN;

// === helpers ===
const toAbs = (p) => {
  if (!p) return null;
  if (/^https?:\/\//i.test(p)) return p;
  const rel = String(p).replace(/^\.\//, '/').replace(/^\./, '/');
  return `${API_HOST}${rel.startsWith('/') ? rel : `/${rel}`}`;
};

// классификатор «дневной» квест
const isDaily = (q) => {
  const d = q?.def || {};
  if (
    d.period === 'daily' || d.type === 'daily' || d.kind === 'daily' ||
    d.reset  === 'daily' || d.daily === true   || q?.daily === true
  ) return true;

  if (q?.deadline) {
    const msLeft = new Date(q.deadline) - new Date();
    return msLeft > 0 && msLeft <= 36 * 60 * 60 * 1000; // запас на TZ/лаг
  }
  return false;
};







// классификатор «квест за аватарку»
const isAvatarQuest = (q) => {
  if (!q) return false;

  const id = q.quest_id || q.id;
  const d  = q.def || {};

  return (
    id === 'core_avatar_upload_1' ||     // id из сервера
    d.id === 'core_avatar_upload_1' ||   // если сервер кладёт id внутрь def
    d.type === 'avatar_uploaded'         // подстраховка по типу
  );
};





const DEFAULT_ICONS = {
  coins : '/uploads/quests/money.png',
  xp    : '/uploads/quests/xp.png',
  skin  : '/uploads/quests/skin.png',
  ticket: '/uploads/quests/ticket.png',
  ticketsByValue: {
    '1':'/uploads/quests/b1.png','5':'/uploads/quests/b5.png',
    '10':'/uploads/quests/b10.png','20':'/uploads/quests/b20.png','50':'/uploads/quests/b50.png',
  }
};

const SECRET_BONUS_ICON = '/img/bonusQuest.png';


// Quest definitions and progress come from the server. Only visible text is
// resolved locally by the stable quest id, so no quest logic is changed.
const getQuestId = (quest) =>
  String(
    quest?.quest_id ||
    quest?.id ||
    quest?.def?.id ||
    ''
  ).trim();

const getQuestText = (
  translate,
  quest,
  field,
  fallback = ''
) => {
  const questId = getQuestId(quest);

  if (!questId) {
    return fallback;
  }

  return translate(
    `questCatalog.${questId}.${field}`,
    { defaultValue: fallback }
  );
};


// иконки конкретных скинов по типу
const SKIN_ICONS_BY_TYPE = {
  // файл лежит в /uploads/quests/wooden_dice.png
  wooden_dice: '/uploads/quests/wooden_dice.png',
  // golden_dice: '/uploads/quests/golden_dice.png', // пример на будущее
};



// ================= Reward chips =================
function RewardChips({ def, hide, revealed = false, className = '' }) {
  const { t: translate } = useTranslation();

  if (!def) return null;

  const chips = [];
  const h = revealed ? {} : (def.ui?.hidden || {});

  // есть ли секретный приз (def.secretPrize), показываем только пока НE revealed
  const hasSecretPrize = !!def.secretPrize && !revealed;

  const mergeRewards = (base, extra) => {
    if (!extra) return base;
    return {
      ...base,
      rewardCoins:   (Number(base.rewardCoins) || 0) + (Number(extra.rewardCoins) || 0),
      rewardXp:      (Number(base.rewardXp)    || 0) + (Number(extra.rewardXp)    || 0),
      rewardTickets: [...(base.rewardTickets || []), ...(extra.rewardTickets || [])],
      rewardSkins:   [...(base.rewardSkins   || []), ...(extra.rewardSkins   || [])],
    };
  };

  // если награда уже раскрыта и есть secretPrize — добавляем его в общие награды
  const effDef = revealed && def.secretPrize ? mergeRewards(def, def.secretPrize) : def;

  const ICONS = {
    coins : effDef.ui?.icons?.coins  ?? DEFAULT_ICONS.coins,
    ticket: effDef.ui?.icons?.ticket ?? DEFAULT_ICONS.ticket,
    xp    : effDef.ui?.icons?.xp     ?? DEFAULT_ICONS.xp,
    skin  : effDef.ui?.icons?.skin   ?? DEFAULT_ICONS.skin,
    ticketsByValue: { ...DEFAULT_ICONS.ticketsByValue, ...(effDef.ui?.icons?.ticketsByValue || {}) }
  };

  // --- монеты ---
  if (effDef.rewardCoins) {
    chips.push({
      key: 'coins', hidden: !!h.coins,
      el: (
        <div className="reward">
          <img className="chip-icon" src={toAbs(ICONS.coins)} alt="coins" />
          <span className="reward-text">{effDef.rewardCoins}</span>
        </div>
      )
    });
  }

  // --- билеты ---
  if (Array.isArray(effDef.rewardTickets)) {
    effDef.rewardTickets.forEach((t, i) => {
      const valKey = String(t.value ?? '');
      const ticketIcon = ICONS.ticketsByValue[valKey] || ICONS.ticket;
      const countText = t.count ? ` (${translate('quests.pieces', { count: t.count })})` : '';

      chips.push({
        key: `ticket-${i}`, hidden: !!h.tickets,
        el: (
          <div className="reward">
            <img
              className="chip-icon"
              src={toAbs(ticketIcon)}
              alt={translate('quests.ticketAlt', { value: valKey })}
            />
            <span className="reward-text">
              ${valKey}{countText}
            </span>
          </div>
        )
      });
    });
  }

  // --- XP ---
  if (effDef.rewardXp) {
    chips.push({
      key: 'xp', hidden: !!h.xp,
      el: (
        <div className="reward">
          <img className="chip-icon" src={toAbs(ICONS.xp)} alt="xp" />
          <span className="reward-text">{effDef.rewardXp}</span>
        </div>
      )
    });
  }

  // --- скины ---
  if (Array.isArray(effDef.rewardSkins)) {
    effDef.rewardSkins.forEach((s, i) => {
      const skinIcon = s.icon || SKIN_ICONS_BY_TYPE[s.type] || ICONS.skin;
      const skinTitle =
        s.label ||
        s.title ||
        translate(`quests.skins.${s.type}`, {
          defaultValue: s.type || translate('quests.skinDefault'),
        });
      const countText = s.count ? ` (${translate('quests.pieces', { count: s.count })})` : '';

      chips.push({
        key: `skin-${i}`, hidden: !!h.skins,
        el: (
          <div className="reward">
            <img className="chip-icon" src={toAbs(skinIcon)} alt={skinTitle} />
            <span className="reward-text">
              {skinTitle}{countText}
            </span>
          </div>
        )
      });
    });
  }

  // видимые фишки
  const visible = hide
    ? []
    : chips
        .filter(c => !c.hidden)
        .map(c => <span key={c.key}>{c.el}</span>);

  // обычная «скрытая награда» (когда просто hide или hidden у наград),
  // но НЕ секретный приз
  const hasHiddenRewards = !revealed && (hide || chips.some(c => c.hidden));

  return (
    <div className={className}>
      {visible}

      {/* 1) СЕКРЕТНЫЙ ПРИЗ: показываем картинку bonusQuest.png */}
      {hasSecretPrize && (
        <div
          className="reward secret"
          title={translate('quests.secretBonusTitle')}
        >
          <img
            className="chip-icon"
            src={SECRET_BONUS_ICON}
            alt={translate('quests.secretPrizeAlt')}
          />
        </div>
      )}

      {/* 2) Обычная скрытая награда: оставляем значок «?» как было */}
      {!hasSecretPrize && hasHiddenRewards && (
        <div className="reward secret" title={translate('quests.hiddenReward')}>
          ?
        </div>
      )}
    </div>
  );
}

// ================= Countdown =================
function Countdown({ deadline }) {
  const { t: translate } = useTranslation();
  const [timeLeft, setTimeLeft] = useState('');
  useEffect(() => {
    if (!deadline) return;
    const tick = () => {
      const diff = new Date(deadline) - new Date();
      if (diff <= 0) { setTimeLeft(translate('quests.timeExpired')); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(translate('quests.duration', { hours: h, minutes: m, seconds: s }));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadline, translate]);
  if (!deadline) return null;
  return <span className="countdown"> · {translate('quests.remaining')}: {timeLeft}</span>;
}

// ================= Details Panel =================
function DetailsPanel({ quest, onClaim }) {
  const { t: translate } = useTranslation();

  if (!quest) return <div className="details-panel dp-empty"><div className="dp-empty-text">{translate('quests.selectQuest')}</div></div>;
  const d = quest.def || {};
  const questTitle = getQuestText(
    translate,
    quest,
    'title',
    d.title || quest.quest_id
  );
  const questDescription = getQuestText(
    translate,
    quest,
    'desc',
    d.desc || d.description || ''
  );
  const hero = toAbs(d.ui?.hero) || toAbs(d.ui?.thumb);
  // const isRevealed = quest.status === 'completed' || quest.status === 'claimed';
  const isRevealed = quest.status === 'claimed';
  const hideRewards = d.ui?.hideRewards && !isRevealed;
  const target = Math.max(1, Number(d.target || 1));
  const prog   = Math.min(target, Number(quest.progress || 0));
  const pct    = Math.round((prog / target) * 100);

  return (
    <div className="details-panel is-linked">
      <div className="dp-content">
        {/* <div className="dp-header">
          {hero ? <img className="dp-hero" src={hero} alt="" /> : <div className="dp-hero dp-hero--stub">{translate('quests.noImage')}</div>}
          <div className="dp-lead">
            <h3 className="dp-title">{d.title || quest.quest_id}</h3>
            {d.desc ? <p className="dp-desc">{d.desc}</p> : null}
            <div className="dp-progress-row">
              <div className="dp-progress-label">
                {translate('quests.progress')} — {prog}/{target}
                {quest.deadline && <Countdown deadline={quest.deadline} />}
              </div>
              <div className="dp-bar"><span className="dp-bar-fill" style={{ width: `${pct}%` }} /></div>
              {quest.status === 'completed' && (
                <button className="btn btn-primary dp-claim-underbar" onClick={(e) => onClaim(quest, e.currentTarget)}>
                  {translate('quests.claimReward')}
                </button>
              )}
            </div>
          </div>
        </div> */}
        <div className="dp-header">
  <div className="dp-hero-wrap">
    {hero
      ? <img className="dp-hero" src={hero} alt="" />
      : <div className="dp-hero dp-hero--stub">{translate('quests.noImage')}</div>
    }
  </div>

  <div className="dp-lead">
    <h3 className="dp-title">{questTitle}</h3>
    {questDescription ? <p className="dp-desc">{questDescription}</p> : null}

    <div className="dp-progress-row">
      <div className="dp-progress-label">
        {translate('quests.progress')} — {prog}/{target}
        {quest.deadline && <Countdown deadline={quest.deadline} />}
      </div>

      <div className="dp-bar">
        <span className="dp-bar-fill" style={{ width: `${pct}%` }} />
      </div>

      {quest.status === 'completed' && (
        <button
          className="btn btn-primary dp-claim-underbar"
          onClick={(e) => onClaim(quest, e.currentTarget)}
        >
          {translate('quests.claimReward')}
        </button>
      )}
    </div>
  </div>
</div>

        <div className="dp-block">
          <div className="dp-rewards-title">{translate('quests.rewards')}</div>
          <RewardChips def={d} hide={hideRewards} revealed={isRevealed} className="dp-rewards-list" />
        </div>
      </div>
    </div>
  );
}

// ================= Main =================
export default function Quests() {
  const { t: translate } = useTranslation();
  const [quests, setQuests]       = useState(null);
  const [error , setError ]       = useState('');
  const [selected, setSelected]   = useState(null);
  const [tab, setTab]             = useState('active'); // 'active' | 'daily' | 'done'
  const [lastClaimedId, setLastClaimedId] = useState(null);
  const { play }                  = useSound();
  const claimingRef               = useRef(false);
  const token                     = localStorage.getItem('token') || '';

  // На проекте глобальный viewport не меняем.
  // Только пока открыта страница квестов включаем реальную ширину устройства,
  // чтобы мобильные @media (440px, 768px и т.д.) действительно срабатывали.
  useEffect(() => {
    let viewport = document.querySelector('meta[name="viewport"]');
    const createdHere = !viewport;
    const previousContent = viewport?.getAttribute('content') ?? null;

    if (!viewport) {
      viewport = document.createElement('meta');
      viewport.setAttribute('name', 'viewport');
      document.head.appendChild(viewport);
    }

    viewport.setAttribute(
      'content',
      'width=device-width, initial-scale=1, viewport-fit=cover'
    );

    return () => {
      if (createdHere) {
        viewport.remove();
      } else if (previousContent !== null) {
        viewport.setAttribute('content', previousContent);
      }
    };
  }, []);

    // 👇 финальная модалка “Обучение пройдено”
  const [showTrainingCongrats, setShowTrainingCongrats] = useState(false);


  const layoutRef = useRef(null);
  const listRef   = useRef(null);
  const itemRefs  = useRef({});
  const [detailTop, setDetailTop] = useState(0);
  const [now, setNow] = useState(Date.now());


    // 👇 ID квеста за аватарку — подставь свой реальный quest_id


  // первичный fetch
  useEffect(() => {
    fetch(`${API_ROOT}/quests/active`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
      .then((data) => setQuests(Array.isArray(data) ? data : (data?.quests || data?.items || [])))
      .catch(err => setError(String(err)));
  }, [token]);

  // тикаем время — авто-скрытие дневных по дедлайну
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // скрываем ТОЛЬКО дневные, у которых истёк дедлайн
  const isExpired = (q) => {
    if (!isDaily(q) || !q?.deadline) return false;
    const t = new Date(q.deadline).getTime();
    return Number.isFinite(t) && t <= now;
  };

  const base = useMemo(() => (Array.isArray(quests) ? quests : []), [quests]);

  // все активные (без просроченных дневных)
  const activesAll = useMemo(
    () => base.filter(q => !isExpired(q) && (q.status === 'started' || q.status === 'completed')),
    [base, now]
  );

  // дневные активные
  const dailyActives = useMemo(
    () => activesAll.filter(isDaily),
    [activesAll]
  );

  // «Активные» БЕЗ дневных
  const actives = useMemo(
    () => activesAll.filter(q => !isDaily(q)),
    [activesAll]
  );

  // завершённые (без просроченных дневных)
  const doneRaw = useMemo(
    () => base.filter(q => !isExpired(q) && q.status === 'claimed'),
    [base, now]
  );

  // Поднимаем только что заклеймленный квест вверх списка «Завершённые»
  const done = useMemo(() => {
    if (!lastClaimedId) return doneRaw;
    const idx = doneRaw.findIndex(q => q.quest_id === lastClaimedId);
    if (idx <= 0) return doneRaw;
    const arr = doneRaw.slice();
    const [picked] = arr.splice(idx, 1);
    arr.unshift(picked);
    return arr;
  }, [doneRaw, lastClaimedId]);

  // текущий список
  const currentList = useMemo(
    () => (tab === 'daily' ? dailyActives : tab === 'done' ? done : actives),
    [tab, actives, dailyActives, done]
  );

  // валидный selection
  useEffect(() => {
    if (!currentList.length) { setSelected(null); return; }
    const still = selected && currentList.some(q => q.quest_id === selected.quest_id);
    if (!still) setSelected(currentList[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentList]);

  // позиционирование правой панели
  const updateDetailTop = () => {
    const layout = layoutRef.current;
    const list   = listRef.current;
    if (!layout || !list) return;

    if (!selected) { setDetailTop(0); return; }

    const it = itemRefs.current[selected.quest_id];
    if (!it) { setDetailTop(0); return; }

    const layoutRect = layout.getBoundingClientRect();
    const itemRect   = it.getBoundingClientRect();
    const top = Math.max(0, Math.round(itemRect.top - layoutRect.top));
    setDetailTop(top);
  };

  // при смене таба — сброс позиции и скролла
  useEffect(() => {
    setDetailTop(0);
    listRef.current?.scrollTo?.({ top: 0, behavior: 'auto' });
  }, [tab]);

  // если выбор пропал — в начало
  useEffect(() => {
    if (!selected) setDetailTop(0);
  }, [selected]);

  useEffect(() => {
    updateDetailTop();
    const onScroll = () => updateDetailTop();
    const onResize = () => updateDetailTop();
    listRef.current?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      listRef.current?.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [selected, currentList]);

  // claim
  const claim = (quest, buttonEl) => {
    const questId = quest?.quest_id;
    if (!questId || claimingRef.current) return;
    claimingRef.current = true;

    play('registerTournament').catch(() => {});

    fetch(`${API_ROOT}/quests/claim`, {
      method : 'POST',
      headers: { 'Content-Type': 'application/json', Authorization : `Bearer ${token}` },
      body   : JSON.stringify({ questId })
    })
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then((resp) => {
      // звук + полёт наград
      play('questBonus').catch(() => {});
      flyRewardsToHeader({ questDef: quest.def || {}, buttonEl });

      // обновить баланс в шапке без перезагрузки
      if (resp && typeof resp.balance === 'number') {
        const el = document.querySelector('[data-balance-target="cash"]');
        if (el) el.textContent = `$${Number(resp.balance).toFixed(2)}`;
        try { localStorage.setItem('balance', String(resp.balance)); } catch {}
      }

      // обновить уровень/XP в шапке
      try {
        window.dispatchEvent(new CustomEvent('level-updated', {
          detail: { level: resp.level, levelPoints: resp.levelPoints }
        }));
      } catch {}



           // 👇 если это квест за аватарку и мы ещё не показывали финалку — показать модалку
      try {
        if (isAvatarQuest(quest) && !localStorage.getItem('bgq_training_quests_done')) {
          localStorage.setItem('bgq_training_quests_done', '1');
          setShowTrainingCongrats(true);
        }
      } catch (e) {
        console.error('[Quests] training modal:', e);
      }


      // ещё до рефетча: запоминаем свежий клейм, сразу идём во «Завершённые» и сбрасываем позицию
      setLastClaimedId(questId);
      setTab('done');
      setSelected(null);
      setDetailTop(0);
      listRef.current?.scrollTo?.({ top: 0, behavior: 'auto' });

      // рефетч списка
      return fetch(`${API_ROOT}/quests/active`, { headers: { Authorization: `Bearer ${token}` } });
    })
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then((data) => {
      const list = Array.isArray(data)
        ? data
        : (Array.isArray(data?.quests) ? data.quests
          : (Array.isArray(data?.items) ? data.items : []));

      setQuests(list);

      // выделить свежепринесённый квест (он уже будет первым в done)
      const fresh = list.find(x => x.quest_id === questId) || null;
      setSelected(fresh);
      // detailTop оставляем 0 — карточка сверху, панель тоже сверху
    })
    .catch(err => console.error('[Quests] claim:', err))
    .finally(() => { claimingRef.current = false; });
  };

  // render states
  if (error)           return <div style={{ color:'var(--danger)', padding: 20 }}>{translate('quests.authRequired')} {error}</div>;
  if (quests === null) return <div style={{ padding: 20 }}>{translate('quests.loading')}</div>;
  if (!Array.isArray(quests) || quests.length === 0) return <div style={{ padding: 20 }}>{translate('quests.noActive')}</div>;

  return (
    <div className="quests-page">
      <h2 className="quests-title">{translate('quests.title')}</h2>

   {/* 👇 Финальное окно после квеста с аватаркой */}
      {showTrainingCongrats && (
        <div className="quests-training-overlay">
          <div className="quests-training-modal">
            <div className="qtr-title">{translate('quests.training.title')}</div>

            <div className="qtr-text">
              <p>{translate('quests.training.first')}</p>
              <p>{translate('quests.training.second')}</p>
              <p>{translate('quests.training.third')}</p>
            </div>

            <button
              type="button"
              className="qtr-button"
              onClick={() => setShowTrainingCongrats(false)}
            >
              {translate('quests.training.continue')}
            </button>
          </div>
        </div>
      )}

      <div className="quests-tabs tabs-auth">
        <button
          className={`qt${tab === 'active' ? ' active' : ''}`}
          onClick={() => setTab('active')}
          aria-pressed={tab === 'active'}
        >
          {translate('quests.tabs.active')} <span className="qt-count">{actives.length}</span>
        </button>

        <button
          className={`qt${tab === 'daily' ? ' active' : ''}`}
          onClick={() => setTab('daily')}
          aria-pressed={tab === 'daily'}
        >
          {translate('quests.tabs.daily')} <span className="qt-count">{dailyActives.length}</span>
        </button>

        <button
          className={`qt${tab === 'done' ? ' active' : ''}`}
          onClick={() => setTab('done')}
          aria-pressed={tab === 'done'}
        >
          {translate('quests.tabs.completed')} <span className="qt-count">{done.length}</span>
        </button>
      </div>

      <div className="quests-layout" ref={layoutRef}>
        <div className="quests-list" ref={listRef}>
          {currentList.map(q => {
            const d = q.def || {};
            const questTitle = getQuestText(
              translate,
              q,
              'title',
              d.title || q.quest_id
            );
            const questCardDescription = getQuestText(
              translate,
              q,
              'cardDesc',
              d.ui?.cardDesc || d.desc || d.description || ''
            );
            const thumb = toAbs(d.ui?.thumb);
            const isActive = selected?.quest_id === q.quest_id;
            const target = Math.max(1, Number(d.target || 1));
            const prog   = Math.min(target, Number(q.progress || 0));
            const pct    = Math.round((prog / target) * 100);
            const globalPct =
              typeof q.globalPct === 'number'
                ? Math.max(0, Math.min(100, Math.round(q.globalPct)))
                : (q.stats && q.stats.total > 0 ? Math.round((q.stats.completed / q.stats.total) * 100) : null);
            const statusText = q.status === 'completed'
              ? translate('quests.status.completed')
              : q.status === 'claimed'
                ? translate('quests.status.claimed')
                : translate('quests.status.active');

            return (
              <div
                key={q.quest_id}
                ref={(el) => { if (el) itemRefs.current[q.quest_id] = el; }}
                className={`quest-card cold-card${isActive ? ' active' : ''}`}
                onClick={() => setSelected(q)}
                role="button"
                aria-selected={isActive}
              >
                <div className="quest-left">
                  {thumb ? <img className="quest-thumb-lg" src={thumb} alt="" /> : <div className="quest-thumb-lg stub">📜</div>}
                  <div className="q-left-progress-label">
                    {translate('quests.progress')} — {prog}/{target}{q.deadline && <Countdown deadline={q.deadline} />}
                  </div>
                  <div className="q-left-row">
                    <div className="q-left-bar"><span className="q-left-bar-fill" style={{ width: `${pct}%` }} /></div>
                    {globalPct !== null && (
                      <div className="q-left-completion" title={translate('quests.completionTitle')} aria-label={translate('quests.completedByPercent', { percent: globalPct })}>
                        <span className="q-left-completion-label">{translate('quests.completedBy')}</span>
                        <span className="q-left-completion-value">{globalPct}%</span>
                      </div>
                    )}
                  </div>
                  {q.status === 'completed' && (tab === 'active' || tab === 'daily') && (
                    <button
                      className="btn btn-primary claim-inline"
                      onClick={(e) => { e.stopPropagation(); claim(q, e.currentTarget); }}
                    >
                      {translate('quests.claimReward')}
                    </button>
                  )}
                </div>

                <div className="quest-main">
                  <div className="q-head">
                    <div className="q-title-lg">
                      {questTitle.toLocaleUpperCase()} <span className="q-status">({statusText})</span>
                      {isDaily(q) && (
                        <span className="q-badge q-badge--daily" title={translate('quests.dailyTitle')}>{translate('quests.daily')}</span>
                      )}
                    </div>
                    <div className="q-percent">{pct}%</div>
                  </div>
                  {questCardDescription ? <div className="q-desc">{questCardDescription}</div> : null}
                </div>

                {/* На телефонах/узких планшетах подробности раскрываются
                    прямо внутри выбранной карточки. На ПК блок скрыт CSS. */}
                {isActive && (
                  <div
                    className="quest-mobile-details"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DetailsPanel quest={q} onClaim={claim} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="details-col">
          <div className="details-floating" style={{ top: selected ? `${detailTop}px` : 0 }}>
            <div className="details-bridge" />
            <DetailsPanel quest={selected} onClaim={claim} />
          </div>
        </div>
      </div>
    </div>
  );
}
